<template>
  <v-container class="rm-drawer-panel">
    <RightDrawerHeader
      :title="$t('REPAIR_FILE_DETAIL_ACTION_DRAWER.TITLE')"
      :showBorder="false"
      showClose
      @close="closeNewAction"
    />
    <v-row class="rm-drawer-scroll new-actions-content align-content-start">
      <v-col cols="12" class="d-flex flex-column ga-4">
        <p class="secondary-card-title mb-0">
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.SUB_TITLE.CUSTOMER_SUPPORT') }}
        </p>
        <v-btn-secondary
          class="w-100"
          variant="flat"
          :color="actionDisabled.stepper ? 'default' : 'primary'"
          :disabled="actionDisabled.stepper"
          :loading="loading"
          @click="routerPush"
          prepend-icon="mdi-file-outline"
        >
          <p>{{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.CUSTOMER_ESTIMATE_REVISION') }}</p>
        </v-btn-secondary>
        <div class="d-flex align-center justify-center">
          <span
            class="status-dot bg-grey"
            :class="{
              [getDotClass('estimatePackage')]:
                currentRepairFile.status_detail ===
                RepairFileStatusDetailConstant.PRICING_SENT_TO_CUSTOMER
            }"
          />
          <p
            :class="{
              'text-grey':
                !checkRepairFileStatusDetailPricingSentToCustomerRelaunch() ||
                !currentRepairFile.estimate_package_last_relaunch_date
            }"
          >
            {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.LAST_RELAUNCH') }}
            {{
              getFormattedDate(
                currentRepairFile.estimate_package_last_relaunch_date,
                checkRepairFileStatusDetailPricingSentToCustomerRelaunch()
              )
            }}
          </p>
        </div>
      </v-col>

      <v-divider color="default" class="mx-4 my-2" />

      <v-col cols="12" class="d-flex flex-column ga-4">
        <p class="secondary-card-title mb-0">
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.REPAIRER_ESTIMATE') }}
        </p>
        <v-btn-secondary
          class="w-100"
          variant="flat"
          :color="actionDisabled.estimate ? 'default' : 'primary'"
          :disabled="actionDisabled.estimate"
          :loading="loading"
          @click="openEstimateRepairer()"
          prepend-icon="mdi-plus"
        >
          <p>{{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.ADD_ESTIMATE_TO_FILE') }}</p>
        </v-btn-secondary>
        <div class="d-flex align-center justify-center">
          <span
            class="status-dot bg-grey"
            :class="{
              [getDotClass('repairerEstimate')]:
                currentRepairFile.status_detail ===
                RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER
            }"
          />
          <p
            :class="{
              'text-grey':
                !checkRepairFileStatusDetailEstimateSentToCustomerRelaunch() ||
                !currentRepairFile.repairer_estimate_last_relaunch_date
            }"
          >
            {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.LAST_RELAUNCH') }}
            {{
              getFormattedDate(
                currentRepairFile.repairer_estimate_last_relaunch_date,
                checkRepairFileStatusDetailEstimateSentToCustomerRelaunch()
              )
            }}
          </p>
        </div>
        <v-btn-secondary
          class="w-100"
          variant="flat"
          :color="actionDisabled.notifyRepairer ? 'default' : 'primary'"
          :disabled="actionDisabled.notifyRepairer"
          :loading="loading"
          @click="openEstimateRepairerDetail()"
          prepend-icon="mdi-information-variant"
        >
          <p>{{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.NOTIFY_THE_REPAIRER') }}</p>
        </v-btn-secondary>
      </v-col>

      <v-divider color="default" class="mx-4 my-2" />

      <v-col cols="12" class="d-flex flex-column ga-4">
        <p class="secondary-card-title mb-0">
          {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.CLOSE_CASE') }}
        </p>
        <v-btn-secondary
          class="w-100 v-btn--no-hover"
          variant="flat"
          :color="actionDisabled.retrieveEquipment ? 'default' : 'primary'"
          :disabled="actionDisabled.retrieveEquipment"
          :loading="loading"
          @click="openDeliveryClient('close')"
          prepend-icon="mdi-check"
        >
          <p>{{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.RETRIEVE_EQUIPMENT_CUSTOMER') }}</p>
        </v-btn-secondary>
      </v-col>
    </v-row>

    <v-row class="mt-auto flex-grow-0 flex-shrink-0">
      <v-col cols="3">
        <v-btn-secondary
          class="button-square"
          color="default"
          :disabled="!checkPermission(['ADMIN_RENTAL_CREATE']) || repairerReminderLoading"
          :loading="loading"
          size="default"
          @click="redirectToLocationPage()"
        >
          <div class="d-flex flex-column align-center ga-2">
            <v-icon icon="mdi-boomerang" />
            <div class="square-button-label-wrapper">
              <p class="square-button-label line-clamp-3">
                {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.ADD_RENTAL') }}
              </p>
              <v-tooltip activator="parent" location="bottom">
                {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.ADD_RENTAL') }}
              </v-tooltip>
            </div>
          </div>
        </v-btn-secondary>
      </v-col>

      <v-col cols="3">
        <v-btn-secondary
          class="button-square"
          color="default"
          :disabled="!checkPermission(['ADMIN_UPDATE_REPAIR_FILE']) || repairerReminderLoading"
          :loading="repairerReminderLoading"
          size="default"
          @click="sendMailReminderToRepairer()"
        >
          <div class="d-flex flex-column align-center ga-2">
            <v-icon icon="mdi-at" />
            <div class="square-button-label-wrapper">
              <p class="square-button-label line-clamp-3">
                {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.REMINDER_REPAIRER') }}
              </p>
              <v-tooltip activator="parent" location="bottom">
                {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.REMINDER_REPAIRER') }}
              </v-tooltip>
            </div>
          </div>
        </v-btn-secondary>
      </v-col>
      <v-col cols="3">
        <v-btn-secondary
          class="button-square"
          variant="flat"
          :color="checkRepairFileStatusDetailToConfirmCost() ? 'default' : 'primary'"
          :disabled="checkRepairFileStatusDetailToConfirmCost()"
          :loading="loading"
          size="default"
          @click="openDeliveryClient('update')"
        >
          <div class="d-flex flex-column align-center ga-2">
            <v-icon icon="mdi-flag-outline" />
            <div class="square-button-label-wrapper">
              <p class="square-button-label line-clamp-3">
                {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.CONFIRM_COST') }}
              </p>
              <v-tooltip activator="parent" location="bottom">
                {{ $t('REPAIR_FILE_DETAIL_ACTION_DRAWER.BUTTON.CONFIRM_COST') }}
              </v-tooltip>
            </div>
          </div>
        </v-btn-secondary>
      </v-col>
      <v-col cols="3">
        <v-btn-secondary
          class="button-square"
          color="default"
          :loading="loading"
          :disabled="!checkPermission(['ADMIN_CLOSE_REPAIR_FILE']) || repairerReminderLoading"
          size="default"
          @click="deleteRepairFile()"
        >
          <div class="d-flex flex-column align-center ga-2">
            <v-icon icon="mdi-trash-can-outline" />
            <div class="square-button-label-wrapper">
              <p class="square-button-label line-clamp-3">{{ $t('COMMON.BUTTON.DELETE') }}</p>
              <v-tooltip activator="parent" location="bottom">
                {{ $t('COMMON.BUTTON.DELETE') }}
              </v-tooltip>
            </div>
          </div>
        </v-btn-secondary>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RepairFileStatusDetailConstant from '@/constants/repair-module/repair-file/repairFileStatusDetailConstant'
import RepairFileService from '@/services/RepairFileService'
import { displayModal } from '@/utils/modalUtils'
import { displayToast, handleResponseError } from '@/utils/toastUtils'
import { checkPermission } from '@/utils/permissionUtils'
import RightDrawerHeader from '@/components/ui/RightDrawerHeader.vue'

export default {
  name: 'NewActions',
  components: { RightDrawerHeader },
  props: {
    currentRepairFile: Object,
    rentalFileId: Object
  },
  data() {
    return {
      loading: false,
      error: null,
      repairerReminderLoading: false
    }
  },
  computed: {
    actionDisabled() {
      const cannotUpdate = !checkPermission(['ADMIN_UPDATE_REPAIR_FILE'])
      return {
        stepper: this.checkRepairFileStatusDetailToGoStepper() || cannotUpdate,
        estimate:
          this.checkRepairFileStatusDetailToGoEstimateRepairer() ||
          cannotUpdate ||
          this.repairerReminderLoading,
        notifyRepairer:
          this.checkRepairFileStatusDetailToGoEstimateRepairerDetail() ||
          cannotUpdate ||
          this.repairerReminderLoading,
        retrieveEquipment:
          this.checkRepairFileStatusDetailToRetrieveEquipmentCustomer() ||
          cannotUpdate ||
          this.repairerReminderLoading
      }
    },
    RepairFileStatusDetailConstant() {
      return RepairFileStatusDetailConstant
    }
  },
  methods: {
    checkPermission,
    openDeliveryClient(actionType) {
      if (checkPermission(['ADMIN_UPDATE_REPAIR_FILE'])) {
        if (this.rentalFileId != null) {
          this.$emit('openRentalWarningModal')
        }

        this.$emit('openDeliveryClient', actionType)
      } else {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
      }
    },
    openEstimateRepairer() {
      if (checkPermission(['ADMIN_UPDATE_REPAIR_FILE'])) {
        this.$emit('openEstimateRepairer')
      } else {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
      }
    },
    openEstimateRepairerDetail() {
      if (checkPermission(['ADMIN_UPDATE_REPAIR_FILE'])) {
        this.$emit('openEstimateRepairerDetail')
      } else {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
      }
    },
    checkRepairFileStatusDetailToRetrieveEquipmentCustomer() {
      switch (this.currentRepairFile.status_detail) {
        case RepairFileStatusDetailConstant.REPAIR_FILE_CREATED:
        case RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER:
        case RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER:
        case RepairFileStatusDetailConstant.ESTIMATE_CREATED:
        case RepairFileStatusDetailConstant.FORCE_ESTIMATE_CREATED:
        case RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER:
        case RepairFileStatusDetailConstant.FORCE_ESTIMATE_SENT_TO_CUSTOMER:
        case RepairFileStatusDetailConstant.ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER:
        case RepairFileStatusDetailConstant.FORCE_ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER:
        case RepairFileStatusDetailConstant.IN_REPARATION:
        case RepairFileStatusDetailConstant.SENT_BACK_BY_REPAIRER:
        case RepairFileStatusDetailConstant.RECEIPT_AT_STORE:
        case RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER:
          return false
        default:
          return true
      }
    },
    closeNewAction() {
      this.$emit('closeNewAction')
    },
    checkRepairFileStatusDetailToGoStepper() {
      if (checkPermission(['ADMIN_ADD_REPAIR_FILE'])) {
        switch (this.currentRepairFile.status_detail) {
          case RepairFileStatusDetailConstant.FINALIZE_REPAIR_FILE:
            return false
          default:
            return true
        }
      } else {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
      }
    },
    checkRepairFileStatusDetailPricingSentToCustomerRelaunch() {
      return (
        this.currentRepairFile.status_detail ===
        RepairFileStatusDetailConstant.PRICING_SENT_TO_CUSTOMER
      )
    },
    checkRepairFileStatusDetailEstimateSentToCustomerRelaunch() {
      return (
        this.currentRepairFile.status_detail ===
        RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER
      )
    },
    checkRepairFileStatusDetailToGoEstimateRepairer() {
      switch (this.currentRepairFile.status_detail) {
        case RepairFileStatusDetailConstant.REPAIR_FILE_CREATED:
        case RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER:
        case RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER:
        case RepairFileStatusDetailConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED:
          return false
        default:
          return true
      }
    },
    checkRepairFileStatusDetailToGoEstimateRepairerDetail() {
      switch (this.currentRepairFile.status_detail) {
        case RepairFileStatusDetailConstant.ESTIMATE_CREATED:
        case RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER:
          return false
        default:
          return true
      }
    },
    checkRepairFileStatusDetailToConfirmCost() {
      const forbiddenStatuses = [
        RepairFileStatusDetailConstant.REPAIR_FILE_CREATED,
        RepairFileStatusDetailConstant.COLLECTION_TO_REPAIRER,
        RepairFileStatusDetailConstant.ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_CREATED,
        RepairFileStatusDetailConstant.ESTIMATE_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.FORCE_ESTIMATE_SENT_TO_CUSTOMER,
        RepairFileStatusDetailConstant.RECEIPT_AT_STORE,
        RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER,
        RepairFileStatusDetailConstant.TAKEOVER_BY_CUSTOMER,
        RepairFileStatusDetailConstant.NETWORK_BILLING,
        RepairFileStatusDetailConstant.FINISHED,
        RepairFileStatusDetailConstant.ABORT_BY_CUSTOMER,
        RepairFileStatusDetailConstant.DELETED
      ]

      return forbiddenStatuses.includes(this.currentRepairFile.status_detail)
    },
    routerPush() {
      switch (this.currentRepairFile.status_detail) {
        case RepairFileStatusDetailConstant.FINALIZE_REPAIR_FILE:
          this.$router.push({
            name: 'repairFileFinalization',
            params: { repairFileBoomerangId: this.$route.params.repairFileBoomerangId }
          })
          break
        default:
          this.$router.push({
            name: 'repairFileDetail',
            params: { repairFileBoomerangId: this.$route.params.repairFileBoomerangId }
          })
      }
    },
    async deleteRepairFile() {
      const confirm = await displayModal({
        title: this.$t('REPAIR_FILE_DETAIL.MODAL.DELETE_REPAIR_FILE.TITLE'),
        message: this.$t('REPAIR_FILE_DETAIL.MODAL.DELETE_REPAIR_FILE.MESSAGE'),
        warning: this.$t('REPAIR_FILE_DETAIL.MODAL.DELETE_REPAIR_FILE.WARNING'),
        validateButton: this.$t('COMMON.BUTTON.DELETE'),
        cancelButton: this.$t('COMMON.BUTTON.CANCEL'),
        variant: 'destructive'
      })
      if (confirm) {
        try {
          this.loading = true
          const data = {
            repair_file_boomerang_id: this.currentRepairFile.boomerang_id
          }
          await RepairFileService.deleteRepairFile(data)
          displayToast(this.$t('REPAIR_FILE_DETAIL.MESSAGE.DELETE.SUCCESS'), 'success')
          this.$router.push({ name: 'repairFiles' })
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    },
    async showUpdateRepairFileStatusModal() {
      this.$emit('closeNewAction')
      this.$emit('showUpdateRepairFileStatusModal')
    },
    async sendMailReminderToRepairer() {
      try {
        this.repairerReminderLoading = true
        const data = {
          repair_file_boomerang_id: this.currentRepairFile.boomerang_id
        }
        await RepairFileService.sendMailReminderToRepairer(data)
        displayToast(this.$t('REPAIR_FILE_DETAIL_ACTION_DRAWER.TOAST.REMINDER_REPAIRER'), 'success')
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.repairerReminderLoading = false
      }
    },
    getFormattedDate(date, checkRepairFileStatusDetail) {
      if (checkRepairFileStatusDetail === false) {
        return 'XX/XX/XXXX'
      }
      if (date) {
        return new Date(date).toLocaleDateString()
      } else {
        return 'XX/XX/XXXX'
      }
    },
    getDotClass(typeRelaunch) {
      let last_relaunch_date = this.currentRepairFile.estimate_package_last_relaunch_date
      let workingDays = this.currentRepairFile.estimate_package_last_relaunch_date_day_count
      if (typeRelaunch === 'repairerEstimate') {
        last_relaunch_date = this.currentRepairFile.repairer_estimate_last_relaunch_date
        workingDays = this.currentRepairFile.repairer_estimate_last_relaunch_date_day_count
      }

      if (workingDays < 3 && last_relaunch_date) {
        return 'status-dot bg-success'
      } else if (workingDays >= 3 && workingDays < 6 && last_relaunch_date) {
        return 'status-dot bg-warning'
      } else if (workingDays >= 6 && last_relaunch_date) {
        return 'status-dot bg-error'
      }
      return 'status-dot bg-grey'
    },
    redirectToLocationPage() {
      this.$router.push({
        name: 'rentalProductList',
        params: { repairFileBoomerangId: this.currentRepairFile.boomerang_id }
      })
    }
  }
}
</script>

<style scoped>
.new-actions-content {
  flex: 0 1 auto;
}

.status-dot {
  margin-right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.button-square.v-btn {
  display: flex;
  width: 100%;
  height: 110px;
}

.square-button-label {
  width: 100%;
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.square-button-label-wrapper {
  width: 100%;
}
</style>
